import { Alert, H1, H2, H3, PrimaryArrowButton, SubBody } from '@rikstv/shared-components';
import { FC, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Layout, Loader } from '~/components/shared';
import { SupportFooter } from '~/components/shared/support-footer/SupportFooter';
import { useComparableSubscriptions } from '../choose-subscription/useComparableSubscriptions.ts';
import { ispServiceAvailabilityCheck } from './internet.api.ts';
import { useInternetFlowStore } from './useInternetStore';

import useSWRImmutable from 'swr/immutable';
import { trackSelectInternetBundle } from '~/analytics/eventTracker.ts';
import { FlexBox } from '~/components/shared/layout/FlexBox.tsx';
import { DisabledsInputField } from './components/DisabledInputField.tsx';
import { InternetProductCard } from './components/InternetProductCard.tsx';
import { TvPackageOnlyCard } from './components/TvPackageOnlyCard.tsx';
import { TvProductCard } from './components/TvProductCard.tsx';
import { useLazySanityData } from './hooks/useLazySanityData.ts';
import { ReactComponent as NgtLogoSvg } from './icons/ngt-logo.svg';
import { toProductBundle } from './internet.utils.ts';
import { Route_InternetLandingPage } from './InternetLandingPage.tsx';

import classes from './InternetOfferSubPage.module.css';

export const Route_InternetOfferSubPage = 'tilbud';

export const InternetOfferSubPage: FC = () => {
  const { availableProducts, isPending, legalTerms, legalTermsFiber, subscriptions, mappedInternetProducts, selectedAddress } = useOfferPageLogic();
  
  // reset selection on mount
  const resetFlowSelection = useInternetFlowStore(s => s.resetSelection);
  useEffect(() => {
    resetFlowSelection();
  }, [resetFlowSelection]);

  // return null while waiting for redirect that happens in useOfferPageLogic
  if (selectedAddress?.addrType !== 'single') {
    return null;
  }
  if (isPending) {
    return (
      <Layout>
        <Card>
          <Loader text="" />
          <H2 className={classes.centerText}>Nå sjekker vi hva du kan få...</H2>
          <p className={classes.centerText}>Adresse: {selectedAddress.description}</p>
          <br />
        </Card>
      </Layout>
    );
  }
  if (!availableProducts || availableProducts.length === 0) {
    return (
      <>
        <Layout wide>
          <div style={{marginBottom: 'calc(24px + var(--rds-fluid-spacing--0-24))'}}>
          <Card noMargin>
            <div>
              <Alert intent="info" variant="inline">
                Beklager! NextGenTel tilbyr ikke fibernett på denne adressen ennå. Sjekk NextGenTel sin nettside for andre alternativer. Du kan uansett bestille TV-pakke hos oss. 
              </Alert>
            </div>
            <br />
            <DisabledsInputField selectedAddress={selectedAddress} />
          </Card>
          </div>
          <TvPackageOnlyCard noInternetPackages />
        </Layout>
        <SupportFooter />
      </>
    );
  }

  // Display TV+Internet packages
  return (
    <>
      <Layout wide className={classes.container}>
        <div className={classes.successHeader}>
          <H1 style={{textWrap: 'balance'}}>Sett sammen internett- og TV-pakken din</H1>
          <Card color='purple-10' noMargin>
            <div>
              <Alert intent="success" variant="inline">
                NextGenTel leverer internett på denne adressen. Velg pakken som passer deg nedenfor.
              </Alert>
            </div>
            <br />
            <DisabledsInputField selectedAddress={selectedAddress} />
          </Card>
        </div>
        <div className={classes.selectionSection}>
          <Card noMargin>
            <H2 data-testid="internet-selection-header" style={{textAlign: 'center'}}>Velg internett* fra <NgtLogoSvg style={{ height: '1em', marginBottom: '-5px' }} /></H2>
            <br />
            <section className={classes.cardGrid}>
              {mappedInternetProducts.map(({ ngtProduct, sanityProduct }) => (
                <InternetProductCard key={ngtProduct.productId} ngtProduct={ngtProduct} sanityProduct={sanityProduct} />
              ))}
            </section>
            <br />
            <div className={classes.finePrint}>
              <SubBody >{legalTermsFiber}</SubBody>
            </div>
          </Card>
          <Card noMargin>
            <H2 data-testid="tv-selection-header" style={{textAlign: 'center'}}>Velg TV-pakke</H2>
            <br />
            <section>
              <div className={classes.cardGrid}>
                {subscriptions.map((sub) => (
                  <TvProductCard key={sub.campaignUrl} subscription={sub} />
                ))}
              </div>
              <br />
              <div className={classes.finePrint}>
                <SubBody>{legalTerms}</SubBody>
              </div>
              <br />
              <TvPackageOnlyCard />
            </section>
          </Card>
        </div>
      </Layout>
      <CheckoutBar />
      {/* Override phone for specific help around internet */}
      <SupportFooter phoneNumber='210 10 212' />
    </>
  );
};

const CheckoutBar: FC = () => {
  const internet = useInternetFlowStore(s => s.selectedIspProduct);
  const selectedAddress = useInternetFlowStore(s => s.selectedAddress);
  const subscription = useInternetFlowStore(s => s.selectedSubscription);
  const setSelectedInternetProduct = useInternetFlowStore(s => s.setSelectedInternetProduct);
  const valid = !!internet && !!subscription && !!selectedAddress;

  const label = [subscription?.name, internet?.cms?.name].filter(x => x != null).join(' + ') || 'Ingen produkter valgt';
  const price = (subscription?.currentPrice || 0) + (internet?.ngt?.productPrice || 0);

  const navigate = useNavigate();

  const onClickNext = valid ? () => {
    trackSelectInternetBundle({
        bundle: { name: label, price },
        city: selectedAddress.city,
        postal_code: selectedAddress.postalCode,
      });
      setSelectedInternetProduct(toProductBundle(internet.ngt, internet.cms));
      navigate(subscription.campaignUrl);
  } : null;

  return (<div className={classes.checkoutbar} data-testid="internet-checkoutbar">
          <div className={classes.cbContent}>
            <FlexBox justifyContent='space-between' alignItems='center'>
              <FlexBox direction='column' gap={2}>
                <SubBody style={{color: '#ddd'}}>{label}</SubBody>
                <H3 style={{color: 'white'}}>{price},- /mnd i 12 mnd</H3>
              </FlexBox>
              <PrimaryArrowButton data-testid="confirm-selection" disabled={!valid} onClick={() => onClickNext?.()}>
                Neste
              </PrimaryArrowButton>
            </FlexBox>
          </div>
        </div>);
}

const useOfferPageLogic = () => {
  const navigate = useNavigate();
  const selectedAddress = useInternetFlowStore((s) => s.selectedAddress);
  const availableProducts = useInternetFlowStore((s) => s.availableServiceOrderProducts);
  const setAvailabilityResponse = useInternetFlowStore((s) => s.setAvailabilityResponse);
  const comparableSubscriptions = useComparableSubscriptions('kampanje', 'bolig');
  // sort subscriptions by price and filter out the ones we wont need
  const {
    broadbandProducts,
    displayData: { legalTerms, legalTermsFiber },
  } = useLazySanityData();
  const mappedInternetProducts = useMemo(() => {
    if (comparableSubscriptions.data && availableProducts) {
      const mappedInternetProducts = availableProducts.map(p => {
        if (p.productName.startsWith('Fiber 50')) {
          return { ngtProduct: p, sanityProduct: broadbandProducts.find(bp => bp.productKey === 'ngt-fiber-50')! };
        }
        if (p.productName.startsWith('Fiber 75')) {
          return { ngtProduct: p, sanityProduct: broadbandProducts.find(bp => bp.productKey === 'ngt-fiber-75')! };
        }
        if (p.productName.startsWith('Fiber 100')) {
          return { ngtProduct: p, sanityProduct: broadbandProducts.find(bp => bp.productKey === 'ngt-fiber-100')! };
        }
        if (p.productName.startsWith('Fiber 300')) {
          return { ngtProduct: p, sanityProduct: broadbandProducts.find(bp => bp.productKey === 'ngt-fiber-300')! };
        }
        return null;
      }).filter(p => p != null)
      return mappedInternetProducts.sort((a, b) => a.ngtProduct.productPrice - b.ngtProduct.productPrice);
    }
    return [];
  }, [comparableSubscriptions.data, availableProducts, broadbandProducts]);

  const {
    isLoading: isPending,
    data,
    error,
  } = useSWRImmutable(selectedAddress ? [selectedAddress] : null, ([addr]) => ispServiceAvailabilityCheck(addr));
  useEffect(() => {
    if (data) {
      setAvailabilityResponse(data);
    } else if (error) {
      console.error('Error fetching ISP availability:', error);
      setAvailabilityResponse(undefined);
    }
  }, [data, error, setAvailabilityResponse]);

  // Redirect if no address selected
  useEffect(() => {
    if (!selectedAddress) {
      // Redirect if address is not of correct type or null
      // Also when address is reset via button
      navigate(`/${Route_InternetLandingPage}`);
    }
  }, [selectedAddress, navigate]);

  const subscriptions = useMemo(() => {
    return (comparableSubscriptions.data?.filter(sub => sub.name !== 'SportsPakken') ?? []).sort((a, b) => a.currentPrice - b.currentPrice);
  }, [comparableSubscriptions.data]);

  return {
    mappedInternetProducts,
    subscriptions,
    availableProducts,
    isPending,
    legalTerms,
    legalTermsFiber,
    selectedAddress,
  };
};
